import { FormikProps } from "formik";

import {
  CASE_DOCUMENT_STATUS,
  CASE_FIELD_SOURCE_MODE,
  CASE_FILE_KIND,
  CaseDocumentModel,
  CaseFieldSourceModel,
  CaseMedicalField,
  CaseModel,
  MEDICAL_LIST_FIELDS,
} from "@shared/models";
import { getCaseTotalScore } from "@containers/Cases/utils/functions";
import {
  hasMedicalFieldText,
  isMedicalListField,
  listItemsToText,
  textToListItems,
} from "@containers/Cases/utils/caseDocuments";
import { CASE_DOCUMENT_FORM_FIELDS, CASE_DOCUMENT_SYNC_KEYS } from "@containers/Cases/constants/caseDocuments";

const CASE_PERSON_DETAILS_FIELDS = [
  "first_name",
  "last_name",
  "date_of_birth",
  "sex",
  "race",
  "citizenship",
  "middle_name",
  "suffix",
  "ssn",
  "ethnic_origin",
  "state_of_birth",
  "country_of_birth",
  "street",
  "city",
  "state",
  "zip",
  "height",
  "weight",
  "hair_color",
  "eyes_color",
  "education_level",
] as const;

type Field =
  | "usm_number"
  | "district"
  | "docket_number"
  | "total_offense_level"
  | "fbi_number"
  | "foreign_country"
  | "ars_assignment"
  | "judge"
  | "rec_facility"
  | "rec_program"
  | "usm_office"
  | "voluntary_surrender_status"
  | "level_of_severity"
  | "criminal_history_points"
  | "criminal_history_category"
  | "history_of_violence"
  | "history_of_escape"
  | "type_of_detainer"
  | "age"
  | "education_level"
  | "substance_abuse"
  | "public_safety_factors"
  | "security_classification"
  | "remarks"
  | "criminal_history_info"
  | "criminal_history_total_points"
  | "type_of_prison"
  | "general_recidivism"
  | "first_step_act_eligible"
  | "second_step_act_eligible"
  | "risk_of_violence_recidivism"
  | "initial_pattern_score"
  | "recidivism"
  | "mental_health_notes"
  | "medical_history_notes"
  | "drug_alcohol_notes"
  | "conditions"
  | "diagnoses"
  | "mobility_flags"
  | "specialist_needs"
  | "case_notes"
  | "program_recommendations"
  | "drug_program"
  | "vs_date"
  | "vs_location"
  | "month_to_release"
  | "source_doc_date"
  | "source_of_doc"
  | "highest_grade"
  | "omdt_referral"
  | "history_of_escape_status"
  | "total"
  | "plea"
  | "5k1"
  | "offense_sentence"
  | (typeof CASE_PERSON_DETAILS_FIELDS)[number];

const SOURCE_MODE_LABELS: Partial<Record<CASE_FIELD_SOURCE_MODE, string>> = {
  [CASE_FIELD_SOURCE_MODE.PSR]: "PSR",
  [CASE_FIELD_SOURCE_MODE.MEDICAL]: "MEDICAL SUMMARIES",
  [CASE_FIELD_SOURCE_MODE.BOTH]: "PSR + SUMMARIES",
};

const NON_FORM_FIELDS: (keyof CaseModel)[] = ["messages", "field_sources", "case_documents", "pending_choices"];

type CriminalHistoryInfo = {
  count: string;
  description: string;
};

type ExcludeCriminalHistoryInfo = Exclude<Field, "criminal_history_info">;

export type CaseModelFormValues = {
  [key in ExcludeCriminalHistoryInfo]: string | undefined;
} & { criminal_history_info: CriminalHistoryInfo[] };

export const generateFormInitialValues = (caseObj?: CaseModel): CaseModelFormValues => {
  const data = Object.keys(caseObj || {}).reduce((acc, field) => {
    if (caseObj) {
      if (field === "history_of_escape" && caseObj.history_of_escape_status) {
        acc.history_of_escape = `${caseObj.history_of_escape}-${caseObj.history_of_escape_status}`;
        return acc;
      }

      if (NON_FORM_FIELDS.includes(field as keyof CaseModel)) {
        return acc;
      }

      if (field === "criminal_history_info") {
        acc.criminal_history_info = Object.keys(caseObj.criminal_history_info || {}).map((key) => ({
          count: key,
          description: caseObj.criminal_history_info?.[key] || "",
        }));
        return acc;
      }

      if (field === "case_person_details" && caseObj.case_person_details) {
        acc = {
          ...acc,
          ...Object.keys(caseObj.case_person_details || {})?.reduce(
            (acc, field) => {
              acc[field] = String(
                caseObj?.case_person_details?.[field as keyof CaseModel["case_person_details"]] ?? "",
              );
              return acc;
            },
            {} as Record<string, string>,
          ),
        };

        return acc;
      }

      if (isMedicalListField(field)) {
        acc[field] = listItemsToText(caseObj[field]);
        return acc;
      }

      acc[field as keyof Omit<CaseModelFormValues, "criminal_history_info">] = String(
        caseObj[field as keyof CaseModel] ?? "",
      );
    }
    return acc;
  }, {} as CaseModelFormValues);

  data.total = `${getCaseTotalScore(caseObj)}`;

  return data;
};

export const parseValuesToCaseModel = (values: CaseModelFormValues) => {
  const copyValues = { ...values, case_person_details: {} };
  delete copyValues.total;
  copyValues.case_person_details = CASE_PERSON_DETAILS_FIELDS.reduce(
    (acc, field) => {
      acc[field] = copyValues[field as keyof Omit<CaseModelFormValues, "criminal_history_info">] || "";
      if (field === "education_level") {
        acc[field] = Number(copyValues[field as keyof Omit<CaseModelFormValues, "criminal_history_info">]);
      }
      delete copyValues[field as keyof CaseModelFormValues];
      return acc;
    },
    {} as Record<(typeof CASE_PERSON_DETAILS_FIELDS)[number], string | number>,
  );

  const public_safety_factors = Array.isArray(copyValues.public_safety_factors)
    ? copyValues.public_safety_factors.join(",")
    : copyValues.public_safety_factors;
  return {
    ...copyValues,
    level_of_severity: Number(copyValues.level_of_severity),
    voluntary_surrender_status: Number(copyValues.voluntary_surrender_status),
    criminal_history_points: Number(copyValues.criminal_history_points),
    criminal_history_category: Number(copyValues.criminal_history_category),
    history_of_violence: Number(copyValues.history_of_violence),
    history_of_escape: Number(copyValues.history_of_escape?.split("-")[0]),
    history_of_escape_status: copyValues.history_of_escape?.split("-")[1] || null,
    type_of_detainer: Number(copyValues.type_of_detainer),
    age: Number(copyValues.age),
    criminal_history_total_points: Number(copyValues.criminal_history_total_points),
    substance_abuse: Number(copyValues.substance_abuse),
    public_safety_factors,
    criminal_history_info: Array.isArray(copyValues.criminal_history_info)
      ? copyValues.criminal_history_info?.reduce<Record<string, string>>((acc, values) => {
          acc[values.count] = values.description;
          return acc;
        }, {})
      : copyValues.criminal_history_info,
    total_offense_level: Number(copyValues.total_offense_level),
    ...Object.fromEntries(MEDICAL_LIST_FIELDS.map((field) => [field, textToListItems(copyValues[field])])),
  } as unknown as Partial<CaseModel>;
};

/**
 * Source a medical field's saved text counts as; text with no source record counts as PSR, or as typed for a field
 * the PSR doesn't fill. Typed text kept with the summaries' text counts as the summaries', as typed text has no label.
 */
const getFieldSourceMode = (field: CaseMedicalField, source?: CaseFieldSourceModel): CASE_FIELD_SOURCE_MODE | null => {
  if (!source) return isMedicalListField(field) ? null : CASE_FIELD_SOURCE_MODE.PSR;
  if (source.mode === CASE_FIELD_SOURCE_MODE.PENDING) return source.psr_text ? CASE_FIELD_SOURCE_MODE.PSR : null;
  if (source.mode === CASE_FIELD_SOURCE_MODE.BOTH && source.user_text) return CASE_FIELD_SOURCE_MODE.MEDICAL;
  return source.mode;
};

/**
 * Where a medical field stands on low confidence: flagged, verified while its text comes from the summaries, or
 * neither.
 */
export const getMedicalFieldConfidence = (caseObj: CaseModel | null, field: CaseMedicalField) => {
  const source = caseObj?.field_sources?.find((item) => item.field === field);
  const isFromSummaries =
    source?.mode === CASE_FIELD_SOURCE_MODE.MEDICAL || source?.mode === CASE_FIELD_SOURCE_MODE.BOTH;

  return {
    flags: (source?.is_flagged && source.flags) || null,
    verification: (isFromSummaries && !source.is_flagged && source.verification) || null,
  };
};

/** Label of the document a medical field's saved text came from, or null when it has none. */
export const getMedicalFieldSourceLabel = (caseObj: CaseModel | null, field: CaseMedicalField): string | null => {
  if (!caseObj || !hasMedicalFieldText(caseObj, field)) return null;

  const mode = getFieldSourceMode(
    field,
    caseObj.field_sources?.find((source) => source.field === field),
  );
  return (mode && SOURCE_MODE_LABELS[mode]) || null;
};

export const getCaseNotesSourceLabel = (caseObj: CaseModel | null, caseDocuments: CaseDocumentModel[]) => {
  const caseNotes = caseDocuments.find(({ kind }) => kind === CASE_FILE_KIND.NOTES);

  return caseObj?.case_notes && caseNotes?.status === CASE_DOCUMENT_STATUS.PARSED ? "CASE NOTES" : null;
};

/** Whether the case changed only by `syncCaseDocumentFields`, after a document was parsed. */
export const hasOnlyDocumentFieldsChanged = (previousCase: CaseModel, nextCase: CaseModel) =>
  previousCase.id === nextCase.id &&
  (Object.keys(nextCase) as (keyof CaseModel)[]).every(
    (key) => previousCase[key] === nextCase[key] || CASE_DOCUMENT_SYNC_KEYS.includes(key),
  );

/** Puts the fields saved by parsing documents into the form. Unsaved edits are kept, those of these fields too. */
export const syncDocumentFormValues = (
  { values, initialValues, resetForm, setValues }: FormikProps<CaseModelFormValues>,
  caseObj: CaseModel,
) => {
  const savedValues = generateFormInitialValues(caseObj);
  const nextInitialValues = { ...initialValues };
  const nextValues = { ...values };

  CASE_DOCUMENT_FORM_FIELDS.forEach((field) => {
    nextInitialValues[field] = savedValues[field];
    if (values[field] === initialValues[field]) {
      nextValues[field] = savedValues[field];
    }
  });

  resetForm({ values: nextInitialValues });
  setValues(nextValues, false);
};
